import * as XLSX from "xlsx";
import { htmlDocsToPdfBlob, safeFilename, mountOffscreenIframe, waitForIframeReady } from "../pdfRenderer";
import { STATUS_COLORS } from "./reportGeneratorCalc";

/* Compact Excel/PDF writers for the Report Generator.

   Arturo, 2026-09-17: "que se genere un excel compacto... un reporte
   profesional, no debe incluir logo de GFI ni nada de eso, solo el
   tipo de reporte, proveedor o cliente, periodo que comprende" —
   followed the same day by: "dale formato de moneda a las filas de
   dinero... alineadas de manera contable hacia la derecha... agrega
   un total general sumando cajas y monto... dale un formato un poco
   mas profesional al pdf... que sea mas ejecutivo".

   opts shape for both exporters:
     { reportTypeLabel, entityLabel, periodLabel, rows, grandTotal,
       groupLabel, columns: [{key,label,type}], filenameBase }
   `type` is "text" | "number" | "money" — drives number formatting
   and right-alignment (money/number) vs left (text). `grandTotal` is
   the object from reportGeneratorCalc.js's computeGrandTotal — same
   keys as a normal row, rendered as one extra bold row at the bottom.
   `groupLabel` is the header text for the synthetic "period" column
   ReportGenerator.js prepends when Group By isn't "none". */

function sanitizeFilenamePart(s) {
  return String(s || "").replace(/[\\/:*?"<>|]/g, "").trim().slice(0, 60) || "All";
}

/* Arturo, 2026-09-17: "aging no se muestra los headers como en xero,
   debe ser identico" — Xero's own "Accounts Receivable Aging Detail"
   title block: report title / company legal name / "As of {date}" /
   "Aging by due date". Only opts.agingKind (set by ReportGenerator.js
   only for the Aging report) triggers this; every other report type
   keeps the plain one-line meta ("Type — Entity — Period"). */
function agingTitleLines(opts) {
  var es = opts.lang === "es";
  var title = opts.agingKind === "receivables"
    ? (es ? "Detalle de Antigüedad de Cuentas por Cobrar" : "Accounts Receivable Aging Detail")
    : (es ? "Detalle de Antigüedad de Cuentas por Pagar" : "Accounts Payable Aging Detail");
  var asOf = (es ? "Al " : "As of ") + (opts.asOfDate || "");
  var byDueDate = es ? "Antigüedad por fecha de vencimiento" : "Aging by due date";
  return [title, opts.companyName || "", asOf, byDueDate].filter(Boolean);
}

var MONEY_FMT = '"$"#,##0.00;[Red]-"$"#,##0.00';
var NUMBER_FMT = "#,##0;[Red]-#,##0";

/* ======================== Excel ======================== */
export function exportReportXlsx(opts) {
  var columns = opts.columns || [];
  var rows = opts.rows || [];
  /* 2026-09-23, Arturo: "agregale al reporte el nombre de ground-fresh"
     (Shipments report, built while adding the Status column + email-
     to-shipper feature) — the original spec (2026-09-17) deliberately
     left every non-Aging report's meta line without a company name;
     `opts.companyName` stays opt-in per report type (only Shipments
     passes it, see ReportGenerator.js's exportOpts) so every other
     report type's meta line is unchanged. */
  var metaLine = [opts.companyName, opts.reportTypeLabel, opts.entityLabel, opts.periodLabel].filter(Boolean).join(" — ");
  var hasTotal = opts.grandTotal && Object.keys(opts.grandTotal).length > 0;
  var totalLabelCol = columns.findIndex(function (c) { return c.type === "text" || c.type === undefined; });

  var headerRow = columns.map(function (c) { return c.label; });
  var totalRow = hasTotal ? columns.map(function (c, i) {
    if (c.type === "money" || c.type === "number") { var v = opts.grandTotal[c.key]; return v === undefined ? "" : v; }
    if (i === (totalLabelCol >= 0 ? totalLabelCol : 0)) return opts.lang === "es" ? "TOTAL GENERAL" : "GRAND TOTAL";
    return "";
  }) : null;

  /* Arturo, 2026-09-17, after pointing at Xero's own "Accounts
     Receivable Aging Detail" report as the reference: ONE column-
     header row at the top (as always) — a `_entityHeader` marker row
     (inserted by ReportGenerator.js before each shipper/client/
     vendor's rows, entity-name column already dropped from `columns`)
     just becomes a single bold-ish name cell inline, column labels are
     never repeated per section. */
  var bodyRows = rows.map(function (r) {
    if (r._entityHeader) return [r.entityLabel];
    return columns.map(function (c) { var v = r[c.key]; return v === undefined || v === null ? "" : v; });
  });

  var metaRows = opts.agingKind ? agingTitleLines(opts).map(function (line) { return [line]; }) : [[metaLine]];
  var aoa = metaRows.concat([[]], [headerRow], bodyRows);
  var dataStartRow = metaRows.length + 2; /* 0-indexed: meta line(s), 1 blank, then headers */
  if (totalRow) aoa.push(totalRow);

  var ws = XLSX.utils.aoa_to_sheet(aoa);

  /* Number formats — Excel auto-right-aligns real numeric cells, so
     no explicit alignment style is needed (and SheetJS Community
     Edition drops most cell styling on write anyway, per
     seasonComparisonExport.js's documented finding — numFmt is the
     one style property that DOES survive). Applying it to every row
     from dataStartRow onward (not just "real" data rows) is safe even
     across entity-header rows mixed in — numFmt has no visible effect
     on a text/blank cell. */
  var totalDataRows = aoa.length - dataStartRow;
  columns.forEach(function (c, colIdx) {
    if (c.type !== "money" && c.type !== "number") return;
    var fmt = c.type === "money" ? MONEY_FMT : NUMBER_FMT;
    for (var r = 0; r < totalDataRows; r++) {
      var ref = XLSX.utils.encode_cell({ r: dataStartRow + r, c: colIdx });
      if (ws[ref]) ws[ref].z = fmt;
    }
  });

  /* Reasonable column widths so numbers/labels aren't clipped. */
  ws["!cols"] = columns.map(function (c) {
    return { wch: c.type === "text" ? 18 : 13 };
  });

  /* Status breakdown (2026-09-23, Arturo: Shipments report — "totales
     de los distntos status tanto cajas como pallets con porcentaje de
     acuerdo al total recibido?... y por porducto"). Appended below the
     grand total, same data as the on-screen StatusBreakdownPanel and
     the PDF's own cover page — overall totals first, then one small
     block per product. Only present when opts.statusBreakdown was
     passed (Shipments report only). */
  if (opts.statusBreakdown) {
    var sbStartRow = aoa.length;
    var sbRows = [[], [opts.lang === "es" ? "TOTALES POR STATUS" : "TOTALS BY STATUS"]];
    var statusHeader = [opts.lang === "es" ? "Status" : "Status", opts.lang === "es" ? "Cajas" : "Cases", "%", "Pallets", "%"];
    var pushStatusBlock = function (label, block) {
      sbRows.push([label]);
      sbRows.push(statusHeader);
      block.rows.forEach(function (r) { sbRows.push([r.label, r.cases, r.casePct / 100, r.pallets, r.palletPct / 100]); });
      sbRows.push([opts.lang === "es" ? "Total Recibido" : "Total Received", block.totalCases, 1, block.totalPallets, 1]);
      sbRows.push([]);
    };
    pushStatusBlock(opts.lang === "es" ? "Todos los Productos" : "All Products", opts.statusBreakdown);
    (opts.statusBreakdown.byProduct || []).forEach(function (p) {
      pushStatusBlock(p.product, { rows: p.rows, totalCases: p.totalCases, totalPallets: p.totalPallets });
    });
    XLSX.utils.sheet_add_aoa(ws, sbRows, { origin: sbStartRow });
    /* Percent columns (C, E — 0-indexed 2 and 4) as real 0-1 fractions
       with a % number format, same SheetJS-survives-numFmt-only rule
       as the money/number columns above. */
    sbRows.forEach(function (row, sr) {
      if (row.length < 5) return;
      [2, 4].forEach(function (colIdx) {
        var ref = XLSX.utils.encode_cell({ r: sbStartRow + sr, c: colIdx });
        if (ws[ref] && typeof ws[ref].v === "number") ws[ref].z = "0%";
      });
    });
  }

  var wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  var d = new Date();
  var iso = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  var fname = "GFI_Report_" + sanitizeFilenamePart(opts.filenameBase) + "_" + sanitizeFilenamePart(opts.entityLabel) + "_" + iso + ".xlsx";
  XLSX.writeFile(wb, fname);
}

/* ======================== PDF ======================== */
function escapeHtml(v) {
  return String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function fmtMoney(v) {
  var n = parseFloat(v);
  if (isNaN(n)) return "";
  var neg = n < 0;
  var s = "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? "(" + s + ")" : s;
}
function fmtNumber(v) {
  var n = parseFloat(v);
  if (isNaN(n)) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function fmtCell(v, type) {
  if (type === "money") return fmtMoney(v);
  if (type === "number") return fmtNumber(v);
  return v === undefined || v === null ? "" : v;
}

/* "Más ejecutivo" (Arturo, 2026-09-17) — still no logo/branding per
   the original spec, but a real navy header bar, right-aligned
   money/number columns with proper $ formatting, and a bold bordered
   grand-total row instead of the earlier plain grayscale table. */
var NAVY = "#1B3A4B";

function buildReportHtml(opts) {
  var columns = opts.columns || [];
  var rows = opts.rows || [];
  var hasTotal = opts.grandTotal && Object.keys(opts.grandTotal).length > 0;
  var totalLabelCol = columns.findIndex(function (c) { return c.type === "text" || c.type === undefined; });

  var pageOrientation = opts.orientation === "landscape" ? "landscape" : "portrait";
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>';
  h += '@page{size:letter ' + pageOrientation + ';margin:0.45in}';
  h += '*{box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,Helvetica,sans-serif;font-size:9.5px;color:#1a1a1a;margin:0}';
  h += '.header{border-bottom:2.5px solid ' + NAVY + ';padding-bottom:8px;margin-bottom:12px}';
  h += '.report-type{font-size:15px;font-weight:700;color:' + NAVY + ';letter-spacing:0.3px}';
  h += '.meta-sub{font-size:10px;color:#555;margin-top:3px}';
  h += '.meta-sub b{color:#1a1a1a}';
  h += 'table{width:100%;border-collapse:collapse;border:1px solid ' + NAVY + '}';
  h += 'th{text-align:left;font-size:8px;text-transform:uppercase;letter-spacing:0.04em;color:#fff;background:' + NAVY + ';font-weight:600;padding:6px 7px;border:1px solid ' + NAVY + '}';
  h += 'th.num{text-align:right}';
  h += 'td{padding:4px 7px;border:1px solid #c7ccd1;font-size:9.5px;vertical-align:middle}';
  h += 'td.num{text-align:right;font-family:"Courier New",monospace}';
  h += 'tr.data:nth-child(even) td{background:#f7f8fa}';
  h += 'tr.entity-header td{font-weight:700;font-size:10.5px;color:' + NAVY + ';background:#eef2f4;border-top:1.5px solid ' + NAVY + ';border-bottom:1px solid ' + NAVY + ';padding:5px 7px}';
  h += 'tr.total td{border-top:2px solid ' + NAVY + ';border-bottom:1px solid ' + NAVY + ';font-weight:700;background:#eef2f4;padding-top:6px}';
  h += 'tr.total td.num{font-family:"Courier New",monospace}';
  h += '.footer{margin-top:14px;padding-top:6px;border-top:0.5px solid #ddd;font-size:7.5px;color:#999;text-align:right}';
  h += '</style></head><body>';

  h += '<div class="header">';
  if (opts.agingKind) {
    var titleLines = agingTitleLines(opts);
    h += '<div class="report-type">' + escapeHtml(titleLines[0]) + '</div>';
    for (var tl = 1; tl < titleLines.length; tl++) h += '<div class="meta-sub">' + escapeHtml(titleLines[tl]) + '</div>';
  } else {
    h += '<div class="report-type">' + escapeHtml(opts.reportTypeLabel) + '</div>';
    if (opts.companyName) h += '<div class="meta-sub">' + escapeHtml(opts.companyName) + '</div>';
    h += '<div class="meta-sub"><b>' + escapeHtml(opts.entityLabel) + '</b> &nbsp;·&nbsp; ' + escapeHtml(opts.periodLabel) + '</div>';
  }
  h += '</div>';

  h += '<table><thead><tr>';
  columns.forEach(function (c) { h += '<th' + (c.type === "money" || c.type === "number" ? ' class="num"' : '') + '>' + escapeHtml(c.label) + '</th>'; });
  h += '</tr></thead><tbody>';
  rows.forEach(function (r) {
    /* Arturo, 2026-09-17, per Xero's own "Accounts Receivable Aging
       Detail" report: a section header is just the entity's name,
       inline, spanning the row — column labels stay in the one
       <thead> above, never repeated per section. */
    if (r._entityHeader) {
      h += '<tr class="entity-header"><td colspan="' + columns.length + '">' + escapeHtml(r.entityLabel) + '</td></tr>';
      return;
    }
    h += '<tr class="data">';
    columns.forEach(function (c) {
      var isNum = c.type === "money" || c.type === "number";
      /* Status column — colored badge, same palette as the on-screen
         preview (STATUS_COLORS, reportGeneratorCalc.js). 2026-09-23,
         Arturo: "bien pero muestra los colores en el status tambien". */
      if (c.key === "status" && r[c.key]) {
        var sc = STATUS_COLORS[r.statusKey];
        var badge = sc
          ? '<span style="display:inline-block;padding:1.5px 7px;border-radius:8px;font-weight:700;background:' + sc.bg + ';color:' + sc.text + ';border:1px solid ' + sc.border + '">' + escapeHtml(r[c.key]) + '</span>'
          : escapeHtml(r[c.key]);
        h += '<td>' + badge + '</td>';
        return;
      }
      h += '<td' + (isNum ? ' class="num"' : '') + '>' + escapeHtml(fmtCell(r[c.key], c.type)) + '</td>';
    });
    h += '</tr>';
  });
  if (hasTotal) {
    h += '<tr class="total">';
    columns.forEach(function (c, i) {
      var isNum = c.type === "money" || c.type === "number";
      var v = isNum ? fmtCell(opts.grandTotal[c.key], c.type) : (i === (totalLabelCol >= 0 ? totalLabelCol : 0) ? (opts.lang === "es" ? "TOTAL GENERAL" : "GRAND TOTAL") : "");
      h += '<td' + (isNum ? ' class="num"' : '') + '>' + escapeHtml(v) + '</td>';
    });
    h += '</tr>';
  }
  h += '</tbody></table>';
  h += '<div class="footer">' + escapeHtml(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })) + '</div>';
  h += '</body></html>';
  return h;
}

/* Status breakdown cover page (2026-09-23, Arturo, Shipments report:
   "totales de los distntos status tanto cajas como pallets con
   porcentaje de acuerdo al total recibido?... y por porducto"). A
   SEPARATE one-page HTML doc, prepended to the PDF's page array in
   buildReportPdfBlob — deliberately not folded into buildReportHtml's
   own `.header` div, which repeats on every page of the main table
   (that would duplicate the breakdown on every page instead of once).
   Reuses the same colored badges as the main table's Status column. */
/* Weekly stacked-status bar chart (2026-09-23, Arturo: "grafico de
   barras con los colores de cada status acumulados por semana es
   decir semana 39 son su barra de 3 colores indicando cuando fue en
   cada status no con numero sino el color, mas color mayor numero por
   asi decirlo... esta barra agregarla a la primer hoja debajo del
   summary, si hay espacio debajo"). Pure CSS bars (this whole page is
   rasterized by html2canvas anyway, same as every other doc this
   exporter builds) — bar height is each week's total cases scaled
   against the tallest week in view, each bar split into colored
   segments (no numeric labels on the bars themselves, per Arturo's
   own framing: the color band IS the value). A legend below spells
   out which color is which status, since the bars alone don't. */
/* 2026-09-23, Arturo, twice in a row, both against the real rendered
   PDF: (1) "la orientacion de los numeros... estando por ejemplo el 42
   de manera horizontal lo giraras 90 grados no que pusieran un numero
   encima del otro" — writing-mode/text-orientation never actually
   rotated the run as one unit in html2canvas's own text layout (it
   kept splitting "42" onto two lines exactly like before, screenshot-
   confirmed); replaced with a real CSS `transform: rotate(...)` on an
   ordinary horizontally-laid-out label — transforms are well supported
   by html2canvas (it's just a canvas operation), unlike CSS writing-
   direction properties. (2) "mira como se muestras las semanas... agrego
   incluso una linea, debe ser como el segundo anexo" — the reference
   image shows a horizontal axis line separating the BARS from the
   rotated tick labels below it; the labels used to live inside the
   same bordered `.chart` box as the bars (border-bottom underneath
   both), so the line never actually sat between them. Split into two
   siblings: `.chart` (bars only, its own border-bottom IS that axis
   line) and `.chart-labels` (a second flex row below, same per-column
   widths so each rotated label still lines up under its bar). */
function buildWeeklyChartHtml(weeklyChart, lang, title) {
  var es = lang === "es";
  /* Arturo: "reduce el tamaño de las barras" — keeps every chart clear
     of its own title line even when it has very few, very tall bars. */
  var CHART_H = 60;
  var maxTotal = weeklyChart.reduce(function (m, w) { return Math.max(m, w.total); }, 0) || 1;
  var seenStatuses = {};
  var defaultTitle = es ? "Cajas por Status — Semanal" : "Cases by Status — Weekly";
  var h = '<div class="block"><div class="block-title">' + escapeHtml(title || defaultTitle) + '</div>';
  h += '<div class="chart">';
  weeklyChart.forEach(function (w) {
    var barH = Math.max(w.total > 0 ? 2 : 0, Math.round((w.total / maxTotal) * CHART_H));
    h += '<div class="chart-col"><div class="chart-bar" style="height:' + barH + 'px">';
    w.segments.forEach(function (seg) {
      seenStatuses[seg.statusKey] = seg.label;
      var sc = STATUS_COLORS[seg.statusKey];
      var segH = Math.max(1, Math.round((seg.cases / w.total) * barH));
      h += '<div style="height:' + segH + 'px;background:' + (sc ? sc.text : "#9ca3af") + '"></div>';
    });
    h += '</div></div>';
  });
  h += '</div>';
  /* weekLabelShort is just the week number (or a 3-letter month / the
     season name — see chartPeriodOf in reportGeneratorCalc.js);
     weekLabel (the full form) rides along as a hover tooltip. */
  h += '<div class="chart-labels">';
  weeklyChart.forEach(function (w) {
    h += '<div class="chart-label-col"><div class="chart-label" title="' + escapeHtml(w.weekLabel) + '">' + escapeHtml(w.weekLabelShort || w.weekLabel) + '</div></div>';
  });
  h += '</div>';
  h += '<div class="chart-legend">';
  Object.keys(seenStatuses).forEach(function (key) {
    var sc = STATUS_COLORS[key];
    h += '<span class="legend-item"><span class="legend-swatch" style="background:' + (sc ? sc.text : "#9ca3af") + '"></span>' + escapeHtml(seenStatuses[key]) + '</span>';
  });
  h += '</div></div>';
  return h;
}

function buildStatusBreakdownHtml(opts) {
  var es = opts.lang === "es";
  var data = opts.statusBreakdown;
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>';
  h += '@page{size:letter ' + (opts.orientation === "landscape" ? "landscape" : "portrait") + ';margin:0.45in}';
  h += '*{box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,Helvetica,sans-serif;font-size:9.5px;color:#1a1a1a;margin:0}';
  h += '.header{border-bottom:2.5px solid ' + NAVY + ';padding-bottom:8px;margin-bottom:12px}';
  h += '.report-type{font-size:15px;font-weight:700;color:' + NAVY + ';letter-spacing:0.3px}';
  h += '.meta-sub{font-size:10px;color:#555;margin-top:3px}';
  h += '.meta-sub b{color:#1a1a1a}';
  h += '.block{margin-bottom:16px}';
  h += '.block-title{font-size:10.5px;font-weight:700;color:' + NAVY + ';text-transform:uppercase;letter-spacing:0.05em;margin-bottom:5px}';
  h += 'table{width:100%;border-collapse:collapse;border:1px solid ' + NAVY + '}';
  h += 'th{text-align:left;font-size:8px;text-transform:uppercase;letter-spacing:0.04em;color:#fff;background:' + NAVY + ';font-weight:600;padding:5px 7px;border:1px solid ' + NAVY + '}';
  h += 'th.num{text-align:right}';
  h += 'td{padding:3.5px 7px;border:1px solid #c7ccd1;font-size:9.5px}';
  h += 'td.num{text-align:right;font-family:"Courier New",monospace}';
  h += 'tr.total td{border-top:2px solid ' + NAVY + ';font-weight:700;background:#eef2f4}';
  h += '.badge{display:inline-block;padding:1.5px 7px;border-radius:8px;font-weight:700}';
  h += '.chart{display:flex;align-items:flex-end;gap:6px;height:' + 60 + 'px;border-bottom:1.5px solid #8a939c;padding:0 4px}';
  h += '.chart-col{display:flex;flex-direction:column;align-items:center;justify-content:flex-end;flex:1 1 0;min-width:0}';
  h += '.chart-bar{width:60%;display:flex;flex-direction:column-reverse}';
  /* Label row is a SEPARATE sibling below `.chart` (see buildWeeklyChartHtml's
     own comment) — its border-bottom above is the actual axis line the
     bars sit on, matching Arturo's reference image. Same per-column
     flex widths as `.chart-col` so each label still lines up under its
     bar. Each label is rotated with a real `transform` (not writing-
     mode/text-orientation, which html2canvas doesn't reliably honor —
     confirmed against 2 real rendered PDFs, see .chart-label's own
     comment below for the full verification story). */
  h += '.chart-labels{display:flex;gap:6px;padding:0 4px}';
  h += '.chart-label-col{position:relative;height:32px;flex:1 1 0;min-width:0}';
  /* 2026-09-23, Arturo, across 3 real rendered PDFs: "las semanas y
     meses... se leen de arriba hacia abajo y debiera ser de abajo
     hacia arriba" (reference image: label's LAST character sits right
     against the axis line, FIRST character hangs at the bottom — read
     "4" then "2" bottom-to-top). Confirmed two ways that don't rely on
     eyeball judgment (my own earlier screenshot read was wrong once):
     colored <span>s per character in a local html2canvas test, and
     each span's real `getBoundingClientRect()` compared to the axis
     line's Y — both agree `rotate(-90deg)` (not `rotate(90deg)`) puts
     the LAST character closest to the axis.
     `top:4px` (not 0) — same day, Arturo: "only when i generate the
     monthly chart, months overlap the bar and the line, bring them
     down a bit". Root cause, also confirmed by measurement: rotating
     around the DEFAULT center origin means a WIDER pre-rotation box
     (a 3-letter month like "oct" vs a 2-digit week like "42") swings
     further both above AND below that center point — so a week number
     cleared the axis line fine at `top:0` while a month abbreviation's
     rotated top edge poked up past it, into the bars. Nudging the
     whole label down by 4px moves that center point deeper into
     `.chart-label-col`'s reserved height, clearing typical week/month
     labels; an unusually long label (e.g. a full season name) can
     still reach back up into the bars — not the case being fixed here,
     revisit if that turns out to matter in practice. */
  h += '.chart-label{position:absolute;top:4px;left:50%;transform:rotate(-90deg);white-space:nowrap;font-size:7.5px;color:#555}';
  h += '.chart-legend{display:flex;flex-wrap:wrap;gap:10px;margin-top:8px}';
  h += '.legend-item{font-size:8.5px;color:#333;display:inline-flex;align-items:center;gap:4px}';
  h += '.legend-swatch{width:9px;height:9px;border-radius:2px;display:inline-block}';
  h += '</style></head><body>';

  h += '<div class="header">';
  h += '<div class="report-type">' + escapeHtml(opts.reportTypeLabel) + '</div>';
  if (opts.companyName) h += '<div class="meta-sub">' + escapeHtml(opts.companyName) + '</div>';
  h += '<div class="meta-sub"><b>' + escapeHtml(opts.entityLabel) + '</b> &nbsp;·&nbsp; ' + escapeHtml(opts.periodLabel) + '</div>';
  h += '<div class="meta-sub">' + escapeHtml(es ? "Totales por Status" : "Totals by Status") + '</div>';
  h += '</div>';

  /* 2026-09-23, Arturo: "no quiero un simary de all products, cada uno
     es independiente mejor quiero por ejemplo bklackberry summary y su
     grafica debajo, blueberry summary y su grafica debajo" — dropped
     the combined "All Products" table AND chart entirely; each
     product now gets its own self-contained section: its totals table
     immediately followed by its own chart, one product after another
     (never all products' tables first and all charts after). */
  var caseLbl = es ? "Cajas" : "Cases", palletLbl = "Pallets", recvLbl = es ? "Total Recibido" : "Total Received";
  var block = function (title, b) {
    h += '<div class="block"><div class="block-title">' + escapeHtml(title) + '</div>';
    h += '<table><thead><tr><th>Status</th><th class="num">' + caseLbl + '</th><th class="num">%</th><th class="num">' + palletLbl + '</th><th class="num">%</th></tr></thead><tbody>';
    b.rows.forEach(function (r) {
      var sc = STATUS_COLORS[r.statusKey];
      var label = sc
        ? '<span class="badge" style="background:' + sc.bg + ';color:' + sc.text + ';border:1px solid ' + sc.border + '">' + escapeHtml(r.label) + '</span>'
        : escapeHtml(r.label);
      h += '<tr><td>' + label + '</td><td class="num">' + r.cases.toLocaleString() + '</td><td class="num">' + r.casePct + '%</td><td class="num">' + r.pallets.toLocaleString() + '</td><td class="num">' + r.palletPct + '%</td></tr>';
    });
    h += '<tr class="total"><td>' + recvLbl + '</td><td class="num">' + b.totalCases.toLocaleString() + '</td><td class="num">100%</td><td class="num">' + b.totalPallets.toLocaleString() + '</td><td class="num">100%</td></tr>';
    h += '</tbody></table></div>';
  };
  var chartByProduct = {};
  ((opts.weeklyChart && opts.weeklyChart.byProduct) || []).forEach(function (p) { chartByProduct[p.product] = p.weeks; });
  ((data && data.byProduct) || []).forEach(function (p) {
    block(p.product, { rows: p.rows, totalCases: p.totalCases, totalPallets: p.totalPallets });
    var weeks = chartByProduct[p.product];
    if (weeks && weeks.length > 0) h += buildWeeklyChartHtml(weeks, opts.lang, (es ? "Cajas por Status — Semanal — " : "Cases by Status — Weekly — ") + p.product);
  });

  h += '</body></html>';
  return h;
}

var PDF_MARGIN_PT = 32; /* ~0.45in, matches the HTML's own @page margin */
/* Letter portrait/landscape in points (jsPDF's own values for
   format:"letter") — hardcoded rather than instantiating a jsPDF just
   to ask it, since this exporter only ever uses these 2 page shapes. */
var PAGE_DIMS_PT = { portrait: { w: 612, h: 792 }, landscape: { w: 792, h: 612 } };
var PAGE_WIDTH_PX = { portrait: 816, landscape: 1100 };

/* Arturo, 2026-09-17: "los pdf deben estar en orientacion portrait no
   landscape porque ahora se ven muy angostos, solo si el reporte
   tuviera demasiadas columnas entonces cambiar orientacion o en su
   defecto permitirme a mi... seleccionar la orientacion optima" —
   default to Portrait (reads better for reports with few columns, like
   Aging); auto-switch to Landscape only when there are enough columns
   that Portrait would cramp them, unless Arturo explicitly picked one
   from the Orientation selector in ReportGenerator.js. */
var AUTO_LANDSCAPE_COLUMN_THRESHOLD = 7;
function resolveOrientation(opts) {
  if (opts.orientation === "portrait" || opts.orientation === "landscape") return opts.orientation;
  return (opts.columns || []).length > AUTO_LANDSCAPE_COLUMN_THRESHOLD ? "landscape" : "portrait";
}

/* Arturo, 2026-09-17: "al cambiar hojas... la ultima linea sale
   partida, la mitad en la primer hoja y la mitad en la siguiente...
   agregar tambien margenes superior e inferior... y repetir en todas
   las hojas los encabezados" — html2canvas rasterises the whole table
   into one flat bitmap and htmlToPdfBlob then slices that bitmap by
   raw pixel height with zero awareness of row boundaries, so a row
   sitting right at the page-height cutoff gets sawn in half. Fixed by
   pre-splitting the ROWS themselves (measured in a real offscreen
   render, so it reflects actual rendered heights, not an estimate)
   into page-sized chunks, building one full HTML doc per chunk
   (title + column headers repeat every page, since buildReportHtml
   always emits them), and handing the array to htmlDocsToPdfBlob —
   the same "N records = N pages, no mid-record slicing" renderer this
   app already uses for the Sales Report (one bill per sheet). */
async function measureRowHeights(opts, widthPx) {
  var probeHtml = buildReportHtml(opts);
  var iframe = mountOffscreenIframe(probeHtml, widthPx);
  try {
    await waitForIframeReady(iframe);
    var doc = iframe.contentDocument || iframe.contentWindow.document;
    var trs = Array.from(doc.querySelectorAll("tbody tr"));
    var dataCount = (opts.rows || []).length;
    var dataTrs = trs.slice(0, dataCount);
    var totalTr = trs[dataCount]; /* only present when opts.grandTotal was passed */
    var headerOverheadPx = dataTrs.length > 0 ? dataTrs[0].offsetTop : 120;
    var footerEl = doc.querySelector(".footer");
    var footerPx = footerEl ? footerEl.offsetHeight + 14 : 30; /* +14 = its own margin-top */
    return {
      rowHeights: dataTrs.map(function (tr) { return tr.offsetHeight; }),
      totalRowHeight: totalTr ? totalTr.offsetHeight : 0,
      headerOverheadPx: headerOverheadPx,
      footerPx: footerPx,
    };
  } finally {
    document.body.removeChild(iframe);
  }
}

/* Splits `rows` into page-sized chunks given each row's measured
   height and the fixed per-page overhead (repeated header + footer).
   `lastPageExtraPx` reserves room on the FINAL chunk for the grand
   total row, which only ever prints once. Never splits a single row
   across chunks — if one row is somehow taller than a whole page's
   budget, it still gets its own chunk rather than being cut. */
function chunkRowsByHeight(rows, rowHeights, budgetPx, lastPageExtraPx) {
  var chunks = [];
  var chunkHeights = [];
  var current = [];
  var currentHeight = 0;
  rows.forEach(function (r, i) {
    var h = rowHeights[i] || 20;
    if (current.length > 0 && currentHeight + h > budgetPx) {
      chunks.push(current); chunkHeights.push(currentHeight);
      current = []; currentHeight = 0;
    }
    current.push(r);
    currentHeight += h;
  });
  chunks.push(current); chunkHeights.push(currentHeight);
  /* If the total row doesn't fit under the last chunk's remaining
     budget, give it a page of its own instead of letting it overflow
     off the bottom of the last page. */
  if (lastPageExtraPx > 0 && chunkHeights[chunkHeights.length - 1] + lastPageExtraPx > budgetPx) {
    chunks.push([]);
  }
  return chunks;
}

/* Arturo, 2026-09-17: "al generar un reporte dame una previsualizacion
   antes de imprimir o descargarlo y ahi pueda cambiar y ver como se ve
   antes de hacerlo" — split out the actual blob-building so
   ReportGenerator.js can preview it (iframe, same pattern already used
   by SalesReports.js's SalesReportPdfPreview) and let Arturo toggle
   orientation live before ever downloading anything. Returns
   { blob, filename }. */
export async function buildReportPdfBlob(opts) {
  var orientation = resolveOrientation(opts);
  var pageDims = PAGE_DIMS_PT[orientation];
  var widthPx = PAGE_WIDTH_PX[orientation];
  var orientedOpts = { ...opts, orientation: orientation };

  var availHeightPt = pageDims.h - PDF_MARGIN_PT * 2;
  var availWidthPt = pageDims.w - PDF_MARGIN_PT * 2;
  var pxPerPt = widthPx / availWidthPt;
  var targetHeightPx = availHeightPt * pxPerPt;

  var measured = await measureRowHeights(orientedOpts, widthPx);
  var budgetPx = Math.max(50, targetHeightPx - measured.headerOverheadPx - measured.footerPx);
  var hasTotal = opts.grandTotal && Object.keys(opts.grandTotal).length > 0;
  var chunks = chunkRowsByHeight(opts.rows || [], measured.rowHeights, budgetPx, hasTotal ? measured.totalRowHeight : 0);

  var docs = chunks.map(function (chunkRows, i) {
    var isLast = i === chunks.length - 1;
    return buildReportHtml({ ...orientedOpts, rows: chunkRows, grandTotal: isLast ? opts.grandTotal : null });
  });
  /* Status breakdown (2026-09-23) — its own single page, prepended
     before the main table's pages, so it never gets duplicated across
     every page of a multi-page table (see buildStatusBreakdownHtml's
     own comment for why this isn't inside buildReportHtml's header). */
  if (opts.statusBreakdown || opts.weeklyChart) docs = [buildStatusBreakdownHtml(orientedOpts)].concat(docs);

  var blob = await htmlDocsToPdfBlob(docs, { widthPx: widthPx, orientation: orientation, marginPt: PDF_MARGIN_PT });
  var fname = safeFilename("GFI_Report_" + sanitizeFilenamePart(opts.filenameBase) + "_" + sanitizeFilenamePart(opts.entityLabel)) + ".pdf";
  return { blob: blob, filename: fname };
}

/* Direct download, no preview — kept for any caller that wants the
   old one-shot behavior. ReportGenerator.js's "Generate PDF" button no
   longer calls this directly; it opens the preview modal instead. */
export async function exportReportPdf(opts) {
  var built = await buildReportPdfBlob(opts);
  var url = URL.createObjectURL(built.blob);
  var a = document.createElement("a");
  a.href = url; a.download = built.filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
